import type { AgentRun } from "@/lib/api/agent";

export type AgentRunOutcome = "completed" | "failed" | "cancelled";

/** POST /agents/{id}/run is 202 Accepted — the run is recorded and the worker
 *  has started. The Aja nyt spinner must clear on that response. Waiting for
 *  GET /runs cannot work: that GET is served by the same process now busy on
 *  the KB tick, so it hangs for the whole evaluation (or forever if the tick
 *  never yields). */
export const AGENT_TRIGGER_ACCEPT_MS = 15_000;

/** Background watch after 202: refresh last_run_at once the tick actually
 *  finishes. Must not drive the run button — see AGENT_TRIGGER_ACCEPT_MS. */
export const AGENT_RUN_WAIT_MS = 90_000;

/**
 * POST /run returns 202 with `status: "running"` on the AgentRun body — that is
 * the worker's state, not the Aja nyt button's. The button must show done as
 * soon as the POST succeeds.
 */
export function runButtonAfterTriggerPost(accepted: boolean): "done" | "error" {
  return accepted ? "done" : "error";
}

export interface PollAgentRunOptions {
  runId: string;
  agentId: string;
  listRuns: (agentId: string) => Promise<AgentRun[]>;
  /**
   * Checked after every await so a poll started by a click stops the moment the
   * component unmounts — the caller must not apply state for a `"cancelled"` outcome.
   */
  isCancelled: () => boolean;
  sleep?: (ms: number) => Promise<void>;
  /**
   * Caps a hung `listRuns` against the remaining budget. Defaults to real time so
   * tests that inject an instant `sleep` for the poll interval do not also make
   * every fetch look timed out.
   */
  deadlineSleep?: (ms: number) => Promise<void>;
  now?: () => number;
  timeoutMs?: number;
  intervalMs?: number;
}

const DEADLINE_ERR = "agent-run-deadline";

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Reject when `remainingMs` elapses before `promise` settles. Used so a hung
 * POST /run or GET /runs cannot pin the "Käynnissä…" spinner past the budget.
 */
export function awaitWithDeadline<T>(
  promise: Promise<T>,
  remainingMs: number,
  sleep: (ms: number) => Promise<void> = defaultSleep,
): Promise<T> {
  if (remainingMs <= 0) {
    return Promise.reject(new Error(DEADLINE_ERR));
  }
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      fn();
    };
    void sleep(remainingMs).then(() =>
      finish(() => reject(new Error(DEADLINE_ERR))),
    );
    promise.then(
      (value) => finish(() => resolve(value)),
      (err: unknown) => finish(() => reject(err)),
    );
  });
}

export function isAgentRunDeadline(err: unknown): boolean {
  return err instanceof Error && err.message === DEADLINE_ERR;
}

/**
 * Poll an agent run to a terminal outcome, bailing out as soon as `isCancelled()` is true.
 *
 * Returns `"cancelled"` (so the caller skips every state update) if cancellation is observed
 * after any await; otherwise `"completed"` or `"failed"` (failed / timeout / deadline exceeded).
 * A transient `listRuns` error is swallowed and polling continues until the deadline.
 * A hung `listRuns` is capped by the remaining wall-clock budget — the 90s timeout used to
 * only be checked between successful fetches, so a blocked agent event loop spun forever.
 */
export async function pollAgentRunUntilDone(
  opts: PollAgentRunOptions,
): Promise<AgentRunOutcome> {
  const sleep = opts.sleep ?? defaultSleep;
  const deadlineSleep = opts.deadlineSleep ?? defaultSleep;
  const now = opts.now ?? Date.now;
  const timeoutMs = opts.timeoutMs ?? AGENT_RUN_WAIT_MS;
  const intervalMs = opts.intervalMs ?? 2_000;

  const deadline = now() + timeoutMs;
  while (now() < deadline) {
    const wait = Math.min(intervalMs, Math.max(0, deadline - now()));
    await sleep(wait);
    if (opts.isCancelled()) return "cancelled";
    const remaining = deadline - now();
    if (remaining <= 0) break;
    try {
      const runs = await awaitWithDeadline(
        opts.listRuns(opts.agentId),
        remaining,
        deadlineSleep,
      );
      if (opts.isCancelled()) return "cancelled";
      const updated = runs.find((r) => r.id === opts.runId);
      if (updated?.status === "completed") return "completed";
      if (updated?.status === "failed" || updated?.status === "timeout")
        return "failed";
    } catch (err) {
      if (isAgentRunDeadline(err)) return "failed";
      // Network hiccup — keep polling until the deadline.
    }
  }
  return "failed";
}
